import React from 'react'
import { reportError } from '../../utils/errorTracking'

/**
 * App-wide error boundary.
 *
 * Without this, any uncaught render error unmounts the whole React tree and
 * leaves the user staring at a blank white page. This catches the error,
 * shows a recoverable fallback, and keeps a hook for forwarding to an error
 * tracker (e.g. Sentry) in production.
 */
export default class ErrorBoundary extends React.Component {
  state = { error: null }

  static getDerivedStateFromError(error) {
    return { error }
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error('Uncaught render error:', error, info?.componentStack)
    // No-op unless VITE_SENTRY_DSN is configured.
    reportError(error, { react: { componentStack: info?.componentStack } })
  }

  handleReload = () => {
    this.setState({ error: null })
    window.location.reload()
  }

  render() {
    if (!this.state.error) return this.props.children

    return (
      <div className="min-h-screen flex items-center justify-center bg-cream-50 px-4">
        <div className="max-w-md w-full text-center bg-white rounded-2xl shadow-lg p-8">
          <div className="text-5xl mb-4" aria-hidden="true">😵</div>
          <h1 className="text-xl font-bold text-forest-900 mb-2">
            Something went wrong
          </h1>
          <p className="text-sm text-gray-500 mb-6">
            An unexpected error stopped this page from loading. Reloading
            usually fixes it — your data is safe.
          </p>
          <button
            type="button"
            onClick={this.handleReload}
            className="inline-flex items-center justify-center rounded-xl bg-gold-500 px-5 py-2.5 text-sm font-semibold text-forest-900 hover:bg-gold-600 transition-colors"
          >
            Reload page
          </button>
        </div>
      </div>
    )
  }
}
